import { useMemo } from 'react';
import type { CompanyMembership, CompanyRole } from '../companies/types';
import { useAuthStore } from './authStore';
import { selectActiveMembership, useMembershipStore } from './membershipStore';
import type { User } from './types';

export const COMPANY_ROLE_HOME_ROUTE: Record<CompanyRole, string> = {
  company_admin: '/admin',
  dispatcher: '/dispatch',
  technician: '/tech',
};

export function isKnownCompanyRole(value: unknown): value is CompanyRole {
  return value === 'company_admin' || value === 'dispatcher' || value === 'technician';
}

export function getCurrentCompanyMembership(
  user: Pick<User, 'memberships' | 'company_membership_id'> | null,
  activeMembershipId?: string | null,
): CompanyMembership | null {
  if (!user || user.memberships.length === 0) return null;

  return (
    user.memberships.find((membership) => membership.id === activeMembershipId) ??
    user.memberships.find((membership) => membership.id === user.company_membership_id) ??
    user.memberships.find((membership) => membership.is_default) ??
    user.memberships[0]
  );
}

export function getCurrentCompanyRole(
  user: Pick<User, 'memberships' | 'company_membership_id'> | null,
  activeMembershipId?: string | null,
): CompanyRole | null {
  const membership = getCurrentCompanyMembership(user, activeMembershipId);
  return isKnownCompanyRole(membership?.company_role) ? membership.company_role : null;
}

export function canAccessAdminFeatures(role: CompanyRole | null): boolean {
  return role === 'company_admin';
}

/**
 * Dispatcher features are accessible to dispatchers AND to company admins.
 *
 * Rationale: in real small companies the "owner / admin" is also the de-facto
 * dispatcher and routinely creates orders, assigns technicians, and triggers
 * follow-up visits. The previous strict separation
 * (`role === 'dispatcher'`) made it impossible to open `/dispatch/order/new`
 * from an admin screen, which blocked the "create follow-up from a closed
 * order" workflow when the same person wears both hats.
 *
 * This does not weaken security — admins already have strictly more rights
 * over the company than dispatchers via `canAccessAdminFeatures`. It just
 * makes the role hierarchy reflect reality.
 */
export function canAccessDispatcherFeatures(role: CompanyRole | null): boolean {
  return role === 'dispatcher' || role === 'company_admin';
}

export function canAccessTechnicianFeatures(role: CompanyRole | null): boolean {
  return role === 'technician';
}

export function getCompanyRoleHomeRoute(role: CompanyRole | null): string {
  if (!role) return '/';
  return COMPANY_ROLE_HOME_ROUTE[role];
}

export function getDefaultAppRoute(user: User | null, activeMembershipId?: string | null): string {
  if (!user) return '/login';
  if (user.platform_role) return '/superadmin';
  return getCompanyRoleHomeRoute(getCurrentCompanyRole(user, activeMembershipId));
}

export function useCompanyRole() {
  const user = useAuthStore((state) => state.user);
  const loading = useAuthStore((state) => state.loading);
  const initialized = useAuthStore((state) => state.initialized);
  const switching = useMembershipStore((state) => state.switching);
  const activeMembership = useMembershipStore(selectActiveMembership);

  return useMemo(() => {
    const isPlatformUser = Boolean(user?.platform_role);
    const resolvedMembership = getCurrentCompanyMembership(user, activeMembership?.membership_id);
    const companyRole = getCurrentCompanyRole(user, activeMembership?.membership_id);
    const memberships = user?.memberships ?? [];
    const missingMembership = Boolean(user && !isPlatformUser && memberships.length === 0);
    const unknownRole = Boolean(user && !isPlatformUser && memberships.length > 0 && !companyRole);

    return {
      user,
      platformRole: user?.platform_role ?? null,
      companyRole,
      companyMembership: resolvedMembership,
      memberships,
      isPlatformUser,
      missingMembership,
      unknownRole,
      loading: !initialized || loading || switching,
      defaultRoute: getDefaultAppRoute(user, activeMembership?.membership_id),
    };
  }, [activeMembership?.membership_id, initialized, loading, switching, user]);
}
